import React, { useState, useMemo } from 'react';
import { translate } from 'components/Translation';
import styles from 'assets/jss/material-dashboard-pro-react/views/settingsSubpageStyle';
import { makeStyles } from '@material-ui/core';
import { finalize } from 'rxjs/operators';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { BodyS, BodySBold } from 'components/Typography/Typography';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import CustomInput from 'components/CustomInput/CustomInput';
import emailNotValid from 'utils/objectsValidation/emailNotValid';
import CustomButton from 'components/CustomButtons/Button';
import { SimService } from 'services/sim/sim';
// @ts-ignore
import html2pdf from 'html2pdf.js';
import SuccessBox from 'components/SuccessBox/SuccessBox';
import ErrorBox from 'components/ErrorBox/ErrorBox';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IQrResponse {
	qrCode?: string;
	qrLink?: string;
	serialNumber?: string;
}

type status = 'SUCCESSFULLY_SEND' | 'FAILURE';

interface IQrCodeProps {
	setLoader: (loader: boolean) => void;
	esimQrData?: IQrResponse | null | undefined;
	loader?: boolean;
	isEsim?: boolean;
	phoneNumber: string;
}

const QrCode = ({ esimQrData, loader = false, isEsim = false, setLoader, phoneNumber }: IQrCodeProps) => {
	const classes = useStyles();
	const [emailAddress, setEmailAddress] = useState<string>('');
	const [emailStatus, setEmailStatus] = useState<status | undefined>();

	const showAlternativeEmailError = useMemo(() => {
		return emailAddress ? emailNotValid(emailAddress) : false;
	}, [emailAddress]);

	const onEmailAddressChange = (event: React.FormEvent<HTMLInputElement>) => {
		const value = (event.target as HTMLInputElement).value;
		event.persist();
		setEmailAddress(value);
	};

	const sendQrCodeToEmail = () => {
		setEmailStatus(undefined);
		setLoader(true);
		SimService.sendQrEmail({
			msisdn: phoneNumber,
			email: emailAddress,
			serialNumber: esimQrData?.serialNumber,
		})
			.pipe(
				finalize(() => {
					setLoader(false);
					setEmailAddress('');
				})
			)
			.subscribe(
				() => {
					setEmailAddress('');
					setEmailStatus('SUCCESSFULLY_SEND');
				},
				() => {
					setEmailStatus('FAILURE');
				}
			);
	};

	const downloadQrCodeAsPdf = () => {
		const data = document.getElementById('qrCodeContainer')?.innerHTML;
		if (data) {
			html2pdf()
				.from(data)
				.set({
					filename: `QR code for ${phoneNumber}.pdf`,
					margin: 0,
					jsPDF: {
						unit: 'mm',
						format: 'a4',
						orientation: 'portrait',
					},
					html2canvas: {
						height: 1080,
						useCORS: true,
					},
					enableLinks: true,
				})
				.save();
		}
	};

	return (
		<div>
			<ConditionalRender
				show={loader}
				onTrue={() => (
					<div className={classes.circularProgressContainer}>
						<CustomCircularProgress
							loading
							text={translate('numbers.settings.replace.esim.card.message')}
						/>
					</div>
				)}
			/>
			<ConditionalRender
				show={!!esimQrData && isEsim}
				onTrue={() => (
					<>
						<div id="qrCodeContainer">
							<div className={classes.resultDataContainer}>
								<BodySBold>{translate('numbers.settings.esim.card.instruction.title')}</BodySBold>
								<BodyS>{translate('numbers.settings.esim.card.instruction.subtitle')}</BodyS>
								<div className="qrContainer">
									<img
										src={`data:image/png;base64, ${esimQrData?.qrCode}`}
										alt="qrCode"
										style={{ minWidth: '128px', minHeight: '128px' }}
									/>
								</div>
								<ConditionalRender
									show={!!esimQrData?.qrLink}
									onTrue={() => (
										<div className="qrContainer">
											<BodyS>{esimQrData?.qrLink}</BodyS>
										</div>
									)}
								/>
								<div className="qrContainer">
									<BodySBold>
										{translate('numbers.settings.replace.esim.card.qr.code.title', phoneNumber)}
									</BodySBold>
								</div>
							</div>
						</div>

						<div className={classes.resultDataContainer}>
							<CustomInput
								labelText={translate('numbers.settings.esim.card.instruction.email')}
								id="emailAddress"
								helperText={
									showAlternativeEmailError &&
									translate('contact.us.register.ticket.input.email.error')
								}
								inputProps={{
									required: true,
									value: emailAddress,
									type: 'email',
									onChange: onEmailAddressChange,
								}}
								formControlProps={{
									fullWidth: true,
								}}
							/>
							<ConditionalRender
								show={emailStatus === 'SUCCESSFULLY_SEND'}
								onTrue={() => <SuccessBox text={translate('contact.messageSent')} />}
							/>
							<ConditionalRender
								show={emailStatus === 'FAILURE'}
								onTrue={() => <ErrorBox text={translate('error.invalid.request')} />}
							/>
							<div className={classes.formButtonContainer}>
								<CustomButton
									color="tertiary-light"
									disabled={!esimQrData}
									onClick={downloadQrCodeAsPdf}
								>
									{translate('numbers.settings.esim.qr.download.pdf')}
								</CustomButton>
								<CustomButton
									color="primary-light"
									disabled={(showAlternativeEmailError || !emailAddress) && !!esimQrData}
									onClick={sendQrCodeToEmail}
								>
									{translate('send')}
								</CustomButton>
							</div>
						</div>
					</>
				)}
			/>
		</div>
	);
};

export default QrCode;
